import {Observable} from 'rxjs/Observable';
import {HttpClient} from '@angular/common/http';
import {UUID} from 'angular2-uuid';
import {isNullOrUndefined} from 'util';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {NotificationService} from './notification.service';
import {Router} from '@angular/router';
import {UserService} from './user.service';

export interface DataOBJ {
  id: string;
  deleted: boolean;

  getHumanIdentifier(): string;
}

export type FromJSONFunc = (data: any) => any

export class RestService<T extends DataOBJ> {

  readonly NO_ID_ERROR = 'NO_ID_ERROR';

  constructor(private serviceUrl: string, private httpClient: HttpClient, private conversionFunc: FromJSONFunc) {
  }

  getAll(): Observable<Array<T>> {
    return this.httpClient.get<Array<T>>(this.serviceUrl)
      .map((res: Array<T>) => res.map((obj: T) => this.conversionFunc(obj)));
  }

  getAllByCustomerId(customerId: string): Observable<Array<T>> {
    return this.httpClient.get<Array<T>>(this.serviceUrl + `/customer/${customerId}`)
      .map((res: Array<T>) => res.map((obj: T) => this.conversionFunc(obj)));
  }

  getById(id: string): Observable<T> {
    return this.httpClient.get<T>(this.serviceUrl + `/${id}`)
      .map((obj: T) => this.conversionFunc(obj));
  }

  save(obj: T): Observable<T> {
    if (obj.id == null) {
      obj.id = UUID.UUID();
    }
    return this.httpClient.post<T>(this.serviceUrl, obj)
      .map((res: T) => this.conversionFunc(res));
  }

  update(obj: T): Observable<T> {
    if (isNullOrUndefined(obj.id)) {
      return Observable.throw(this.NO_ID_ERROR);
    }
    return this.httpClient.put<T>(this.serviceUrl, obj)
      .map((res: T) => this.conversionFunc(res))
  }

  delete(obj: T): Observable<string> {
    return this.httpClient.delete<any>(this.serviceUrl + `/${obj.id}`)
      .map((res: any) => obj.id);
  }
}


export interface DataObjComponent<T extends DataOBJ> {

  // name of the object in the plural
  readonly namePlural: string;

  // path to go for editing by id
  readonly editPath: string;
}

export class DataObjComponentHelper<T extends DataOBJ> {

  data: BehaviorSubject<Array<T>>;

  dataToShow: T[];

  changeState = new Map<string, boolean>();

  disableDeletion = true;

  disableEditing = true;

  searchText = new BehaviorSubject<string>('');

  searchFc = new FormControl();

  constructor(private service: DataObjService<T>, private ns: NotificationService, private us: UserService,
              private router: Router, private editPath: string) {
    this.data = new BehaviorSubject<Array<T>>([]);
  }

  onInit() {
    this.service.getAllByCustomerId(this.us.currentUser.getValue().customerDatabaseId)
      .subscribe((objects: T[]) => this.data.next(objects.reverse()));
    this.searchFc.valueChanges.debounceTime(250).subscribe((text) => {
      this.searchText.next(text);
      this.dataToShow = this.filterData(text, this.data.getValue());
    });
    this.data.subscribe((objects: Array<T>) => {
      this.dataToShow = this.filterData(this.searchText.getValue(), objects);
    });
  }

  changeSelection(id: string, state: boolean) {
    this.changeState.set(id, state);
    this.disableDeletion = true;
    this.disableEditing = true;
    let selectionCount = 0;
    this.changeState.forEach((value, key) => {
      if (value) {
        this.disableDeletion = false;
        selectionCount++;
      }
    });
    if (selectionCount === 1) {
      this.disableEditing = false;
    }
  }

  getSelection(id: string): boolean {
    const value = this.changeState.get(id);
    if (value) {
      return value;
    } else {
      return false;
    }
  }

  getSelectedIds(): Array<string> {
    const returnValue: Array<string> = [];
    this.changeState.forEach((value, key) => {
      if (value) {
        returnValue.push(key);
      }
    });
    return returnValue;
  }

  filterData(t: string, c: T[]): T[] {
    return c.filter((obj) => {
      for (const i in obj) {
        if (obj.hasOwnProperty(i) && !isNullOrUndefined(obj[i])) {
          // if (i === 'number') console.log(`Testing ${(obj[i] as Object).toString()}`);
          if ((obj[i] as Object).toString().toLowerCase().indexOf(t.toLowerCase()) !== -1) {
            return true;
          }
        }
      }
      return false;
    });
  }

  bulkDelete(ids: Array<string>) {
    let resultObservable: Observable<string> = Observable.from(new Array<string>());
    ids.forEach((id) => {
      resultObservable = resultObservable.concat(
        this.service.delete(this.data.getValue().find((data) => data.id === id))
      );
    });
    resultObservable.subscribe(
      (id) => {
        console.log(`Id ${id} successfully deleted`);
        const deletedObj = this.data.getValue().find((obj) => obj.id === id);
        this.data.next(this.data.getValue().filter((obj) => obj.id !== id));
        this.changeSelection(id, false);
        this.ns.warning(deletedObj.getHumanIdentifier(), 'Deletado com sucesso!');
      },
      (error) => {
        console.log(`error: ${error}`);
        this.ns.error(error.toString(), 'Erro ao deletar');
      }
    );
  }

  editData() {
    const selection = this.getSelectedIds();
    this.router.navigateByUrl(this.editPath + selection[0]);
  }
}

export class DataObjEditComponentHelper<T extends DataOBJ> {

  formGroups: FormGroup[];

  formErrors: any[];

  formInvalid: boolean[];

  disableSubmit = false;

  loading = false;

  editing = false;

  actualObj: T;

  constructor(private fb: FormBuilder, private controlsConfigs: any[], private validationMessages: any[],
              private entityPath: string, private router: Router, private ns: NotificationService,
              private service: DataObjService<T>,
              private parent: DataObjEditComponent<T>) {
    this.formInvalid = new Array<boolean>(controlsConfigs.length);
  }

  buildForm() {
    this.formGroups = this.controlsConfigs.map((config) => this.fb.group(config));
    this.formErrors = this.controlsConfigs.map((config) => new Object());
  }

  onValueChanges(data: any, index: number) {
    this.validateFormsFields(this.formGroups[index], this.validationMessages[index], index).subscribe((value) => {
      this.formErrors[index] = value;
    })
  }

  validateFormsFields(formGroup: FormGroup, validationMessage: any, index: number): Observable<any> {
    const result = {};
    for (const field in validationMessage) {
      if (validationMessage.hasOwnProperty(field)) {
        const control = formGroup.get(field);
        const messages = validationMessage[field];
        for (const key in control.errors) {
          if (control.errors.hasOwnProperty(key) && !control.pristine) {
            console.log(`Error: ${key}`);
            if (!result[field]) {
              result[field] = [];
            }
            result[field].push(messages[key]);
          }
        }
      }
    }
    this.formInvalid[index] = false;
    return this.parent.validateFields(index, result);
  }

  preSave() {
    this.loading = true;
    this.disableSubmit = true;
  }

  postSave() {
    this.loading = false;
    this.disableSubmit = false;
  }

  isAllValid(index: number): Observable<boolean> {
    if (this.formGroups[index].valid) {
      return this.validateFormsFields(this.formGroups[index], this.validationMessages[index], index).map((value) => {
        this.formErrors[index] = value;
        if (Object.keys(value).length !== 0) {
          return false;
        } else {
          return true;
        }
      })
    } else {
      return Observable.of(false);
    }
  }

  processResult(index: number, obj: T) {
    console.log(`Object salvo com sucesso: '${obj}'`);
    this.ns.success(obj.getHumanIdentifier(), 'Salvo com sucesso');
    this.formGroups[index].reset();
    this.buildForm();
    if (this.editing) {
      this.router.navigateByUrl(this.entityPath);
    } else {
      this.router.navigateByUrl(`${this.entityPath}_new`);
    }
  }

  decideOperation(obj: T): Observable<T> {
    if (this.editing) {
      return this.service.update(obj);
    } else {
      return this.service.save(obj);
    }
  }

  getSelectActiveObject(id: string, objs: Select2Data[]): Select2Data[] {
    if (!isNullOrUndefined(id)) {
      console.log(`Searching for id: ${id}`);
      const selected = objs.find((obj) => obj.id === id);
      console.log(`Setting the active object to ${JSON.stringify(selected)}`);
      return [selected]
    } else {
      return []
    }
  }
}

export interface DataObjEditComponent<T extends DataOBJ> {

  // vowel of genre plus the name on the singular. Ex: 'o Servidor'
  readonly vowelSingular: string;

  readonly entityPath: string;

  readonly controlsConfigs: any[];

  validateFields(index: number, defaultFormErrors: any): Observable<any>;

  trySave(index: number, model: any);
}


export interface DataObjService<T extends DataOBJ> {

  getAll(): Observable<T[]>;

  getAllByCustomerId(customerId: string): Observable<T[]>;

  getById(id: string): Observable<T>;

  save(obj: T): Observable<T>;

  update(obj: T): Observable<T>;

  delete(obj: T): Observable<string>;
}

export interface CountResponse {
  count: number;
}

export interface Select2Data {
  id: string;
  text: string;
}

export interface ChartData {
  name: string;
  value: number;
}



// WEBPACK FOOTER //
// /Users/tabelini/development/jvm/tenacity/pysix/PysixCloud/src/app/rest-service.ts